/**
 * Generates initials from a course name for avatar display.
 * Single word: first two letters. Multiple words: first letter of first and last words.
 */
export function getCourseInitials(courseName: string): string {
  if (!courseName) return '??';
  const cleanName = courseName.replace(/[^a-zA-Z\s]/g, '').trim();
  if (!cleanName) return '??';

  const words = cleanName.split(/\s+/).filter(word => word.length > 0);
  if (words.length === 0) return '??';

  if (words.length === 1) {
    const word = words[0].toUpperCase();
    return word.length >= 2 ? word.substring(0, 2) : word;
  }
  return words[0][0].toUpperCase() + words[words.length - 1][0].toUpperCase();
}

export type LearnerCourseStatus =
  | 'not-enrolled' | 'enrolled' | 'in-progress' | 'completed' | 'overdue' | 'enrollment-expired';

/**
 * KAN-411 / KAN-418: the SINGLE gate-aware learner course status, shared by the
 * My Courses card (course-list) AND the dashboard "Your Courses" widget so they
 * can never diverge.
 *
 * KAN-418: completion now keys on the persisted gate-aware marker
 * course_progress.passed_at, surfaced as `computed_status='completed'` /
 * `passedAt` by the backend (db-course-operations derives computed_status from
 * passed_at at the source). So this resolver trusts that marker directly instead
 * of re-deriving from module progress (computedProgress>=100) or certificate
 * presence. The KAN-411 behaviors are preserved by construction:
 *   - gate-blocked course (100% modules, failed quiz): backend passed_at NULL ->
 *     computed_status 'in-progress' -> In Progress here.
 *   - opt-out course: gate short-circuits -> passed_at set -> Completed.
 *   - pre-fix certified learner: passed_at backfilled from completed_at -> Completed.
 * The Cert BADGE stays keyed on an actual certificate elsewhere; `hasCertificate`
 * is retained for call-site compatibility but no longer gates completion here.
 */
export function resolveCourseCompletionStatus(course: any, _hasCertificate: boolean = false): LearnerCourseStatus {
  if (course?.computedStatus === 'completed' || course?.passedAt != null) {
    return 'completed';
  }
  if (course?.computedStatus) {
    switch (course.computedStatus) {
      case 'in-progress': return 'in-progress';
      case 'overdue': return 'overdue';
      case 'enrollment-expired': return 'enrollment-expired';
      case 'enrolled': return 'enrolled';
      default: return 'enrolled';
    }
  }
  return course?.startedAt ? 'in-progress' : 'enrolled';
}
